import { forwardRef, MouseEventHandler, ReactElement } from 'react'
import { IconButton as MuiIconButton, styled, Tooltip, TooltipProps } from '@mui/material'
import { tokens } from 'ComponentLibrary'
import { IconContext } from '@phosphor-icons/react'

type IconButtonVariants = 'primary' | 'destructive' | 'secondary' | 'tertiary'

export type IconButtonProps = {
    size: 'small' | 'medium'
    icon: ReactElement
    variant?: IconButtonVariants
    onClick: MouseEventHandler<HTMLButtonElement>
    ariaLabel?: string
    disabled?: boolean
    tooltip?: string
    tooltipPlacement?: TooltipProps['placement']
    markedYellow?: boolean
    markedGreen?: boolean
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
    (
        {
            size,
            icon,
            variant = 'primary',
            onClick,
            ariaLabel,
            disabled,
            markedYellow,
            markedGreen,
            tooltip,
            tooltipPlacement = 'top',
            ...rest
        },
        ref
    ) => {
        return (
            <Tooltip title={tooltip} placement={tooltipPlacement} describeChild={!!ariaLabel} PopperProps={{ sx: { zIndex: 2_000 } }}>
                <span style={{ width: size === 'small' ? '32px' : '40px', height: size === 'small' ? '32px' : '40px' }}>
                    <StyledIconButton
                        size={size}
                        $variant={variant}
                        $markedYellow={markedYellow}
                        $markedGreen={markedGreen}
                        onClick={onClick}
                        disableRipple
                        aria-label={ariaLabel}
                        disabled={disabled}
                        ref={ref}
                        {...rest}
                    >
                        <IconContext.Provider
                            value={{
                                size: size === 'small' ? tokens.SizingIconS : tokens.SizingIconM,
                                weight: size === 'small' ? 'bold' : 'regular',
                            }}
                        >
                            {icon}
                        </IconContext.Provider>
                    </StyledIconButton>
                </span>
            </Tooltip>
        )
    }
)

const StyledIconButton = styled(MuiIconButton, {
    shouldForwardProp: (prop: string) => prop[0] !== '$',
})<{ $variant: IconButtonVariants; $markedYellow?: boolean; $markedGreen?: boolean }>(
    ({
        $variant,
        $markedYellow,
        $markedGreen,
    }: {
        $variant: keyof typeof ICON_BUTTON_VARIANTS
        $markedYellow?: boolean
        $markedGreen?: boolean
    }) => ({
        padding: tokens.SizingSpacingNone,
        borderRadius: tokens.SizingCornerS,
        ...($markedYellow
            ? {
                  backgroundColor: '#FDFBE6',
                  boxShadow: `inset 0 0 0 ${tokens.SizingBorderS} #F2EF5A`,
                  color: tokens.ColorIconBoldest,
              }
            : $markedGreen
              ? {
                    backgroundColor: tokens.ColorSurfaceSelectedDefault,
                    boxShadow: `inset 0 0 0 ${tokens.SizingBorderS} ${tokens.ColorBorderSelectedDefault}`,
                    color: tokens.ColorIconLinkSelected,
                }
              : ICON_BUTTON_VARIANTS[$variant].default),
        '&:hover': $markedGreen
            ? {
                  backgroundColor: tokens.ColorSurfaceHover,
                  boxShadow: `inset 0 0 0 ${tokens.SizingBorderS} ${tokens.ColorBorderSelectedDefault}`,
                  color: tokens.ColorIconLinkSelected,
              }
            : {
                  ...ICON_BUTTON_VARIANTS[$variant].hover,
                  ...($markedYellow ? { boxShadow: `inset 0 0 0 ${tokens.SizingBorderS} #F2EF5A` } : {}),
              },
        '&:active': $markedGreen
            ? {
                  backgroundColor: tokens.ColorSurfacePressed,
                  boxShadow: `inset 0 0 0 ${tokens.SizingBorderS} ${tokens.ColorBorderSelectedDefault}`,
                  color: tokens.ColorIconLinkSelected,
              }
            : {
                  ...ICON_BUTTON_VARIANTS[$variant].pressed,
              },
        '&.MuiIconButton-sizeMedium': {
            width: '40px',
            height: '40px',
        },
        '&.MuiIconButton-sizeSmall': {
            width: '32px',
            height: '32px',
        },
        '&.Mui-disabled': {
            color: tokens.ColorIconDisabled,
            backgroundColor: tokens.ColorSurfaceDisabled,
            boxShadow: 'none',
        },
    })
)

const ICON_BUTTON_VARIANTS = {
    primary: {
        default: {
            backgroundColor: tokens.ColorSurfacePositiveBoldDefault,
            color: tokens.ColorIconInverse,
        },
        hover: { backgroundColor: tokens.ColorSurfacePositiveBoldHover, color: tokens.ColorIconInverse },
        pressed: { backgroundColor: tokens.ColorSurfacePositiveBoldPressed, color: tokens.ColorIconInverse },
    },
    destructive: {
        default: {
            backgroundColor: tokens.ColorSurfaceDangerBoldDefault,
            color: tokens.ColorIconInverse,
        },
        hover: {
            backgroundColor: tokens.ColorSurfaceDangerBoldHover,
            color: tokens.ColorIconInverse,
        },
        pressed: {
            backgroundColor: tokens.ColorSurfaceDangerBoldPressed,
            color: tokens.ColorIconInverse,
        },
    },
    secondary: {
        default: {
            backgroundColor: tokens.ColorSurfaceDefault,
            color: tokens.ColorIconBoldest,
            boxShadow: `inset 0 0 0 ${tokens.SizingBorderS} ${tokens.ColorBorderDefault}`,
        },
        hover: {
            backgroundColor: tokens.ColorSurfaceHover,
            color: tokens.ColorIconBoldest,
            boxShadow: 'none',
        },
        pressed: {
            backgroundColor: tokens.ColorSurfacePressed,
            color: tokens.ColorIconBoldest,
            boxShadow: 'none',
        },
    },
    tertiary: {
        default: {
            color: tokens.ColorIconBoldest,
        },
        hover: {
            backgroundColor: tokens.ColorSurfaceHover,
            color: tokens.ColorIconBoldest,
        },
        pressed: {
            backgroundColor: tokens.ColorSurfacePressed,
            color: tokens.ColorIconBoldest,
        },
    },
}
